import { useEffect, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";
import api from "@/lib/api";
import { getItem, setItem } from "@/lib/safeStorage";

const KEY = "staff_alert_muted";
const NOTES = [880, 1318.5];
let ctx = null;
let seen = null;
let seenFor = null;
let pending = false;
let wavUrl = null;

function getCtx() {
  if (!ctx || ctx.state === "closed") {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
  }
  return ctx;
}

// Same chime rendered as an in-memory WAV, used when Web Audio is blocked.
function getWavUrl() {
  if (wavUrl) return wavUrl;
  const rate = 22050;
  const len = Math.floor(rate * 0.85);
  const buf = new ArrayBuffer(44 + len * 2);
  const v = new DataView(buf);
  const str = (o, s) => [...s].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  str(0, "RIFF"); v.setUint32(4, 36 + len * 2, true); str(8, "WAVEfmt ");
  v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, rate, true); v.setUint32(28, rate * 2, true); v.setUint16(32, 2, true);
  v.setUint16(34, 16, true); str(36, "data"); v.setUint32(40, len * 2, true);
  for (let i = 0; i < len; i++) {
    const t = i / rate;
    let s = 0;
    NOTES.forEach((f, n) => {
      const lt = t - n * 0.18;
      if (lt >= 0 && lt < 0.65) s += Math.sin(2 * Math.PI * f * lt) * Math.min(1, lt / 0.02) * Math.exp(-lt * 9) * 0.4;
    });
    v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s)) * 32767, true);
  }
  wavUrl = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
  return wavUrl;
}

function unlock() {
  const c = getCtx();
  if (!c) return;
  const done = () => {
    if (pending) {
      pending = false;
      playChime();
    }
  };
  if (c.state !== "running") c.resume().then(done).catch(() => {});
  else done();
}

function schedule(c) {
  const now = c.currentTime + 0.02;
  NOTES.forEach((freq, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    const start = now + i * 0.18;
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.4, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.6);
    osc.connect(gain).connect(c.destination);
    osc.start(start);
    osc.stop(start + 0.65);
  });
}

function playFallback() {
  const a = new Audio(getWavUrl());
  return a.play().catch(() => {
    pending = true;
  });
}

export function playChime() {
  const c = getCtx();
  if (!c) return playFallback();
  if (c.state === "running") return schedule(c);
  const wait = new Promise((res) => setTimeout(res, 300));
  Promise.race([c.resume(), wait])
    .then(() => (c.state === "running" ? schedule(c) : playFallback()))
    .catch(playFallback);
}

export function useAlertWatcher(userKey) {
  useEffect(() => {
    if (seenFor !== userKey) {
      seen = null;
      seenFor = userKey;
    }
    const events = ["pointerdown", "touchstart", "keydown", "click"];
    events.forEach((e) => window.addEventListener(e, unlock));
    let alive = true;
    const poll = async () => {
      const [r, o] = await Promise.allSettled([api.get("/requests?status=pending"), api.get("/orders")]);
      if (!alive) return;
      const ids = [];
      if (r.status === "fulfilled") r.value.data.forEach((x) => ids.push(`r${x.id}`));
      if (o.status === "fulfilled") o.value.data.forEach((x) => ids.push(`o${x.id}`));
      if (!ids.length && r.status === "rejected" && o.status === "rejected") return;
      const hasNew = seen && ids.some((id) => !seen.has(id));
      seen = new Set([...(seen || []), ...ids]);
      if (hasNew && getItem(KEY) !== "1") playChime();
    };
    poll();
    const id = setInterval(poll, 3000);
    return () => {
      alive = false;
      clearInterval(id);
      events.forEach((e) => window.removeEventListener(e, unlock));
    };
  }, [userKey]);
}

export function AlertSoundToggle({ testId = "sound-toggle-btn" }) {
  const [muted, setMuted] = useState(getItem(KEY) === "1");
  const toggle = () => {
    const next = !muted;
    setMuted(next);
    setItem(KEY, next ? "1" : "0");
    if (!next) playChime();
  };
  const Icon = muted ? VolumeX : Volume2;
  return (
    <button
      onClick={toggle}
      data-testid={testId}
      aria-pressed={!muted}
      title={muted ? "Sound off" : "Sound on"}
      className={`flex items-center justify-center border border-[#262626] p-2 transition-colors hover:bg-[#161616] ${muted ? "text-zinc-500" : "text-[#32d74b]"}`}
    >
      <Icon size={16} />
    </button>
  );
}
